/**
 * Catalog of the user guides the app can open (WI 2887). The single source of
 * truth for the UI (which guide a link opens) and the web route (which files it
 * may serve). Handlers look a request up here and 404 on a miss, so nothing
 * outside the catalog ever reaches the filesystem.
 *
 * `filename` is the file inside the guides directory: `docs/gcc/` in the dev
 * tree, `GUIDES_DIR` (`/app/guides`) in the web container. The files in `docs/`
 * are the only copies; the Dockerfile copies them into the image. The web route
 * serves each guide under its filename, so the guides' relative links to each
 * other work the same in the app as in the repo.
 */
export interface GuideCatalogEntry {
  /** Stable identifier the UI asks for. */
  id: string
  /** Title shown to the user. */
  title: string
  /** Filename inside the guides directory, and the last part of its URL. */
  filename: string
}

export const GUIDE_CATALOG: readonly GuideCatalogEntry[] = [
  {
    id: 'gcc-excel-input',
    title: 'Preparing your Excel taxonomy',
    filename: 'gcc_excel_input_guide.html',
  },
  {
    id: 'gcc-user-manual',
    title: 'GCC Design Engine: user manual',
    filename: 'gcc_user_manual.html',
  },
  {
    id: 'gcc-tn-branching-manual',
    title: 'Retained & Combined TN Analyses: user manual',
    filename: 'gcc_tn_branching_user_manual.html',
  },
]

/** The guide the GCC Step 1 Client Taxonomy upload links to. */
export const GCC_EXCEL_GUIDE_ID = 'gcc-excel-input'

/** The guides listed on the GCC Settings page, in catalog order: the two manuals. */
export const GCC_SETTINGS_GUIDES: readonly GuideCatalogEntry[] = GUIDE_CATALOG.filter((g) =>
  ['gcc-user-manual', 'gcc-tn-branching-manual'].includes(g.id),
)

/** Resolve a catalog entry by id. Returns undefined for unknown ids. */
export function getGuideById(id: string): GuideCatalogEntry | undefined {
  return GUIDE_CATALOG.find((g) => g.id === id)
}

/** Resolve a catalog entry by its filename (the web route's lookup). */
export function getGuideByFilename(filename: string): GuideCatalogEntry | undefined {
  return GUIDE_CATALOG.find((g) => g.filename === filename)
}
